import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, ExternalLink, Globe, Minus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "offence-guides", label: "Offence Guides" },
  { value: "arrest-rights", label: "Arrest & Rights" },
  { value: "bail-process", label: "Bail Process" },
  { value: "costs-and-scams", label: "Costs & Scams" },
  { value: "perspectives", label: "Perspectives" },
];

const wc = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover: "",
  category: "bail-process",
  read_time: "5 min read",
  published: true,
  seo_title: "",
  meta_description: "",
  primary_keyword: "",
  pillar_slug: "",
  related_slugs: [],
  tldr: "",
  faq: [],
  author_id: "",
  reviewer_id: "",
  published_at: "",
  og_image: "",
  sources: [],
  noindex: false,
  bns: "",
  ipc: "",
  bnss: "",
  crpc: "",
};

// Client-side mirror of the backend validation
function validateForm(f) {
  const errs = [];
  if (!f.title.trim()) errs.push("Title is required");
  if (f.seo_title && f.seo_title.length > 60)
    errs.push(`SEO title must be 60 characters or fewer (now ${f.seo_title.length})`);
  if (f.meta_description && !(f.meta_description.length >= 140 && f.meta_description.length <= 155))
    errs.push(`Meta description must be 140–155 characters (now ${f.meta_description.length})`);
  if (f.slug && (f.slug.length > 60 || !SLUG_RE.test(f.slug)))
    errs.push("Slug must be lowercase letters/numbers joined by hyphens, 60 characters or fewer");
  const rel = f.related_slugs.filter((s) => s.trim());
  if (rel.length && !(rel.length >= 3 && rel.length <= 5))
    errs.push("Related articles must contain 3–5 slugs");
  rel.forEach((s) => {
    if (!SLUG_RE.test(s.trim())) errs.push(`Related slug "${s}" must be lowercase-hyphenated`);
  });
  if (f.tldr && !(wc(f.tldr) >= 40 && wc(f.tldr) <= 60))
    errs.push(`TL;DR must be 40–60 words (now ${wc(f.tldr)})`);
  const faq = f.faq.filter((x) => x.question.trim() || x.answer.trim());
  if (faq.length && !(faq.length >= 4 && faq.length <= 6))
    errs.push("FAQ must contain 4–6 items");
  faq.forEach((x, i) => {
    if (!x.question.trim()) errs.push(`FAQ item ${i + 1}: question is required`);
    if (wc(x.answer) > 50) errs.push(`FAQ item ${i + 1}: answer must be 50 words or fewer (now ${wc(x.answer)})`);
  });
  const sources = f.sources.filter((s) => s.title.trim() || s.url.trim());
  sources.forEach((s, i) => {
    if (!s.title.trim() || !s.url.trim()) errs.push(`Source ${i + 1}: needs both title and URL`);
  });
  return errs;
}

// Empty optional fields become null so they are omitted, not stored as ""
function payloadFromForm(f) {
  const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const sections = { bns: csv(f.bns), ipc: csv(f.ipc), bnss: csv(f.bnss), crpc: csv(f.crpc) };
  const hasSections = Object.values(sections).some((a) => a.length);
  const rel = f.related_slugs.map((s) => s.trim()).filter(Boolean);
  const faq = f.faq.filter((x) => x.question.trim() || x.answer.trim());
  const sources = f.sources.filter((s) => s.title.trim() || s.url.trim());
  const str = (v) => (v && v.trim() ? v.trim() : null);
  return {
    title: f.title.trim(),
    slug: str(f.slug),
    excerpt: f.excerpt,
    content: f.content,
    cover: f.cover,
    category: f.category,
    read_time: f.read_time,
    published: f.published,
    seo_title: str(f.seo_title),
    meta_description: str(f.meta_description),
    primary_keyword: str(f.primary_keyword),
    pillar_slug: str(f.pillar_slug),
    related_slugs: rel.length ? rel : null,
    tldr: str(f.tldr),
    faq: faq.length ? faq : null,
    author_id: str(f.author_id),
    reviewer_id: str(f.reviewer_id),
    published_at: str(f.published_at),
    og_image: str(f.og_image),
    sections_cited: hasSections ? sections : null,
    sources: sources.length ? sources : null,
    noindex: f.noindex,
  };
}

export default function PostsAdmin({ token }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // post or "new"
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [wpStatus, setWpStatus] = useState(null);

  const auth = { "X-Admin-Token": token };

  const load = async () => {
    setLoading(true);
    try {
      const [pr, wr] = await Promise.all([
        fetch(`${BACKEND}/api/admin/posts`, { headers: auth }),
        fetch(`${BACKEND}/api/admin/wordpress`, { headers: auth }),
      ]);
      setPosts(await pr.json());
      setWpStatus(await wr.json());
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setRow = (k, i, key, val) =>
    setForm((f) => {
      const rows = [...f[k]];
      rows[i] = key ? { ...rows[i], [key]: val } : val;
      return { ...f, [k]: rows };
    });
  const addRow = (k, blank, max) =>
    setForm((f) => (f[k].length >= max ? f : { ...f, [k]: [...f[k], blank] }));
  const delRow = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, j) => j !== i) }));

  const startEdit = (p) => {
    setEditing(p);
    const csv = (a) => (Array.isArray(a) ? a.join(", ") : "");
    setForm({
      ...empty,
      title: p.title || "",
      slug: p.slug || "",
      excerpt: p.excerpt || "",
      content: p.content || "",
      cover: p.cover || "",
      category: p.category || "bail-process",
      read_time: p.read_time || "5 min read",
      published: p.published ?? true,
      seo_title: p.seo_title || "",
      meta_description: p.meta_description || "",
      primary_keyword: p.primary_keyword || "",
      pillar_slug: p.pillar_slug || "",
      related_slugs: Array.isArray(p.related_slugs) ? p.related_slugs : [],
      tldr: p.tldr || "",
      faq: Array.isArray(p.faq) ? p.faq.map((x) => ({ question: x.question || "", answer: x.answer || "" })) : [],
      author_id: p.author_id || "",
      reviewer_id: p.reviewer_id || "",
      published_at: p.published_at ? String(p.published_at).slice(0, 10) : "",
      og_image: p.og_image || "",
      sources: Array.isArray(p.sources) ? p.sources.map((s) => ({ title: s.title || "", url: s.url || "" })) : [],
      noindex: !!p.noindex,
      bns: csv(p.sections_cited?.bns),
      ipc: csv(p.sections_cited?.ipc),
      bnss: csv(p.sections_cited?.bnss),
      crpc: csv(p.sections_cited?.crpc),
    });
  };

  const startNew = () => {
    setEditing("new");
    setForm(empty);
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const save = async () => {
    const errs = validateForm(form);
    if (errs.length) {
      toast.error(errs[0], errs.length > 1 ? { description: `${errs.length - 1} more issue${errs.length > 2 ? "s" : ""} — check the counters` } : undefined);
      return;
    }
    setSaving(true);
    try {
      let resp;
      const body = JSON.stringify(payloadFromForm(form));
      if (editing === "new") {
        resp = await fetch(`${BACKEND}/api/admin/posts`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...auth },
          body,
        });
      } else {
        resp = await fetch(`${BACKEND}/api/admin/posts/${editing.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...auth },
          body,
        });
      }
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(typeof j.detail === "string" ? j.detail : "Save failed");
      }
      toast.success(editing === "new" ? "Post created" : "Post saved");
      cancel();
      load();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const r = await fetch(`${BACKEND}/api/admin/posts/${id}`, {
        method: "DELETE",
        headers: auth,
      });
      if (!r.ok) throw new Error();
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="posts-admin">
      {/* WordPress status banner */}
      {wpStatus && (
        <div
          className={`mb-6 rounded-2xl border p-5 flex items-start gap-4 ${
            wpStatus.configured
              ? "border-[#2C4C3B]/30 bg-[#2C4C3B]/5"
              : "border-[#D8CDBA] bg-[#EAE6DF]/50"
          }`}
          data-testid="wp-status"
        >
          <Globe className="text-[#2C4C3B] flex-shrink-0 mt-0.5" size={20} strokeWidth={1.5} />
          <div className="flex-1 text-sm">
            {wpStatus.configured ? (
              <>
                <div className="font-medium text-[#1A1A1A]">WordPress connected</div>
                <div className="text-[#4A4640]">
                  Feed: <code className="text-xs bg-white px-2 py-0.5 rounded border border-[#D8CDBA]">{wpStatus.url}</code>
                  {" "}· {wpStatus.posts_available} posts pulled
                </div>
              </>
            ) : (
              <>
                <div className="font-medium text-[#1A1A1A]">WordPress not connected</div>
                <div className="text-[#4A4640]">
                  Set <code className="text-xs bg-white px-2 py-0.5 rounded border border-[#D8CDBA]">WORDPRESS_URL</code> in backend .env to pull posts from a WP site. Local posts below always work.
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {!editing && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div className="text-[#736F66] text-sm">
              {posts.length} local post{posts.length === 1 ? "" : "s"}
            </div>
            <button onClick={startNew} className="btn-primary text-sm" data-testid="post-new">
              <Plus size={16} /> New post
            </button>
          </div>

          <div className="bg-white border border-[#D8CDBA] rounded-3xl overflow-hidden">
            <table className="w-full text-sm" data-testid="posts-table">
              <thead className="bg-[#EAE6DF] text-[#4A4640]">
                <tr>
                  <th className="text-left p-4">Title</th>
                  <th className="text-left p-4">Category</th>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="p-8 text-center text-[#736F66]">Loading…</td></tr>
                )}
                {!loading && posts.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-[#736F66]">No posts yet. Create your first one.</td></tr>
                )}
                {posts.map((p) => (
                  <tr key={p.id} className="border-t border-[#D8CDBA]" data-testid={`post-row-${p.id}`}>
                    <td className="p-4">
                      <div className="font-medium text-[#1A1A1A]">{p.title}</div>
                      <div className="text-xs text-[#736F66] mt-0.5">/{p.slug}</div>
                    </td>
                    <td className="p-4">{p.category}</td>
                    <td className="p-4">{p.date}</td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 text-xs rounded-full ${
                        p.published ? "bg-[#2C4C3B]/10 text-[#2C4C3B]" : "bg-[#EAE6DF] text-[#736F66]"
                      }`}>{p.published ? "published" : "draft"}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <a
                          href={`/blog?p=${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#4A4640] hover:text-[#2C4C3B]"
                          title="View"
                          data-testid={`post-view-${p.id}`}
                        >
                          <ExternalLink size={16} />
                        </a>
                        <button
                          onClick={() => startEdit(p)}
                          className="text-[#4A4640] hover:text-[#2C4C3B]"
                          title="Edit"
                          data-testid={`post-edit-${p.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => del(p.id)}
                          className="text-[#A83F32] hover:text-[#8E3428]"
                          title="Delete"
                          data-testid={`post-delete-${p.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && (
        <div className="bg-white border border-[#D8CDBA] rounded-3xl p-8" data-testid="post-editor">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif-display text-3xl font-medium">
              {editing === "new" ? "New post" : "Edit post"}
            </h3>
            <button onClick={cancel} className="text-[#736F66] hover:text-[#1A1A1A]" data-testid="post-editor-close">
              <X size={22} />
            </button>
          </div>

          <datalist id="post-slugs">
            {posts.map((p) => (
              <option key={p.id} value={p.slug} />
            ))}
          </datalist>

          <div className="space-y-5">
            <Field label="Title">
              <input value={form.title} onChange={set("title")} className={inp} data-testid="post-input-title" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Slug (URL)" hint="Leave empty to auto-generate from title. Changing an existing slug 301-redirects the old URL.">
                <input
                  value={form.slug}
                  onChange={set("slug")}
                  className={inp}
                  placeholder="my-blog-post"
                  data-testid="post-input-slug"
                />
                <Count n={form.slug.length} max={60} unit="chars · lowercase-hyphens" testid="count-slug" />
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={set("category")} className={inp} data-testid="post-input-category">
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                  {form.category && !CATEGORIES.some((c) => c.value === form.category) && (
                    <option value={form.category}>{form.category} (legacy)</option>
                  )}
                </select>
              </Field>
            </div>

            <Field label="Cover image URL">
              <input value={form.cover} onChange={set("cover")} className={inp} placeholder="https://..." data-testid="post-input-cover" />
              {form.cover && (
                <img src={form.cover} alt="preview" className="mt-3 h-32 rounded-xl object-cover border border-[#D8CDBA]" />
              )}
            </Field>

            <Field label="Excerpt (short summary shown on the blog list)">
              <textarea value={form.excerpt} onChange={set("excerpt")} rows={2} className={`${inp} resize-none`} data-testid="post-input-excerpt" />
            </Field>

            <Field label="Content (HTML supported)" hint="Use <p>, <h2>, <h3>, <strong>, <em>, <ul><li>, <a>, <img> etc.">
              <textarea value={form.content} onChange={set("content")} rows={14} className={`${inp} font-mono text-sm resize-y`} data-testid="post-input-content" />
            </Field>

            <SectionHead>SEO</SectionHead>

            <Field label="SEO title" hint="Shown as the Google result title. Leave empty to use the post title.">
              <input value={form.seo_title} onChange={set("seo_title")} className={inp} data-testid="post-input-seo-title" />
              <Count n={form.seo_title.length} max={60} unit="chars" testid="count-seo-title" />
            </Field>

            <Field label="Meta description" hint="Shown as the Google result snippet. Leave empty to use the excerpt.">
              <textarea value={form.meta_description} onChange={set("meta_description")} rows={2} className={`${inp} resize-none`} data-testid="post-input-meta-description" />
              <Count n={form.meta_description.length} min={140} max={155} unit="chars" testid="count-meta-description" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Primary keyword" hint="The main search phrase this post targets">
                <input value={form.primary_keyword} onChange={set("primary_keyword")} className={inp} data-testid="post-input-primary-keyword" />
              </Field>
              <Field label="Pillar post slug" hint="The parent guide this post supports, if any">
                <input value={form.pillar_slug} onChange={set("pillar_slug")} className={inp} list="post-slugs" placeholder="bail-process" data-testid="post-input-pillar-slug" />
              </Field>
            </div>

            <Field label="Social share image URL (og:image)" hint="Leave empty to use the cover image">
              <input value={form.og_image} onChange={set("og_image")} className={inp} placeholder="https://..." data-testid="post-input-og-image" />
              {form.og_image && (
                <img src={form.og_image} alt="og preview" className="mt-3 h-32 rounded-xl object-cover border border-[#D8CDBA]" />
              )}
            </Field>

            <SectionHead>TL;DR</SectionHead>
            <Field label="Quick answer box" hint="Shown in a box right under the title. 40–60 words.">
              <textarea value={form.tldr} onChange={set("tldr")} rows={3} className={`${inp} resize-none`} data-testid="post-input-tldr" />
              <Count n={wc(form.tldr)} min={40} max={60} unit="words" testid="count-tldr" />
            </Field>

            <SectionHead>FAQ (4–6)</SectionHead>
            <div className="space-y-4" data-testid="post-faq-editor">
              {form.faq.map((x, i) => (
                <div key={i} className="rounded-2xl border border-[#D8CDBA] p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-[#736F66]">Question {i + 1}</span>
                    <button type="button" onClick={() => delRow("faq", i)} className="text-[#A83F32]" data-testid={`faq-del-${i}`}>
                      <Minus size={16} />
                    </button>
                  </div>
                  <input value={x.question} onChange={(e) => setRow("faq", i, "question", e.target.value)} className={inp} placeholder="Question" data-testid={`faq-q-${i}`} />
                  <textarea value={x.answer} onChange={(e) => setRow("faq", i, "answer", e.target.value)} rows={2} className={`${inp} resize-none`} placeholder="Answer" data-testid={`faq-a-${i}`} />
                  <Count n={wc(x.answer)} max={50} unit="words" testid={`count-faq-a-${i}`} />
                </div>
              ))}
              {form.faq.length < 6 && (
                <button type="button" onClick={() => addRow("faq", { question: "", answer: "" }, 6)} className="btn-secondary text-sm" data-testid="faq-add">
                  <Plus size={14} /> Add question
                </button>
              )}
            </div>

            <SectionHead>Related articles (3–5)</SectionHead>
            <div className="space-y-3" data-testid="post-related-editor">
              {form.related_slugs.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={s} onChange={(e) => setRow("related_slugs", i, null, e.target.value)} className={inp} list="post-slugs" placeholder="related-post-slug" data-testid={`related-slug-${i}`} />
                  <button type="button" onClick={() => delRow("related_slugs", i)} className="text-[#A83F32]" data-testid={`related-del-${i}`}>
                    <Minus size={16} />
                  </button>
                </div>
              ))}
              {form.related_slugs.length < 5 && (
                <button type="button" onClick={() => addRow("related_slugs", "", 5)} className="btn-secondary text-sm" data-testid="related-add">
                  <Plus size={14} /> Add related article
                </button>
              )}
            </div>

            <SectionHead>Legal sections cited</SectionHead>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                ["bns", "BNS", "e.g. 63, 103"],
                ["ipc", "IPC", "e.g. 302, 420"],
                ["bnss", "BNSS", "e.g. 35, 483"],
                ["crpc", "CrPC", "e.g. 167(2)"],
              ].map(([k, label, ph]) => (
                <Field key={k} label={label} hint="Comma-separated section numbers">
                  <input value={form[k]} onChange={set(k)} className={inp} placeholder={ph} data-testid={`post-input-${k}`} />
                </Field>
              ))}
            </div>

            <SectionHead>Sources</SectionHead>
            <div className="space-y-3" data-testid="post-sources-editor">
              {form.sources.map((s, i) => (
                <div key={i} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input value={s.title} onChange={(e) => setRow("sources", i, "title", e.target.value)} className={inp} placeholder="Source title" data-testid={`source-title-${i}`} />
                  <input value={s.url} onChange={(e) => setRow("sources", i, "url", e.target.value)} className={inp} placeholder="https://…" data-testid={`source-url-${i}`} />
                  <button type="button" onClick={() => delRow("sources", i)} className="text-[#A83F32] self-end sm:self-auto" data-testid={`source-del-${i}`}>
                    <Minus size={16} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addRow("sources", { title: "", url: "" }, 20)} className="btn-secondary text-sm" data-testid="source-add">
                <Plus size={14} /> Add source
              </button>
            </div>

            <SectionHead>Authorship & dates</SectionHead>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Author" hint="Shown as 'Written by …'">
                <input value={form.author_id} onChange={set("author_id")} className={inp} placeholder="Adv. name" data-testid="post-input-author-id" />
              </Field>
              <Field label="Legal reviewer" hint="Shown as 'Legally reviewed by …'">
                <input value={form.reviewer_id} onChange={set("reviewer_id")} className={inp} placeholder="Adv. name" data-testid="post-input-reviewer-id" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Published date" hint="Leave empty for today">
                <input type="date" value={form.published_at} onChange={set("published_at")} className={inp} data-testid="post-input-published-at" />
              </Field>
              {editing !== "new" && editing.updated_at && (
                <Field label="Last updated" hint="Set automatically on every save">
                  <input value={String(editing.updated_at).slice(0, 10)} readOnly className={`${inp} opacity-60`} data-testid="post-input-updated-at" />
                </Field>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Read time">
                <input value={form.read_time} onChange={set("read_time")} className={inp} placeholder="5 min read" data-testid="post-input-readtime" />
              </Field>
              <Field label="Status">
                <label className="flex items-center gap-3 mt-2">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} data-testid="post-input-published" />
                  <span className="text-[#4A4640]">Published (visible on the blog)</span>
                </label>
                <label className="flex items-center gap-3 mt-2">
                  <input type="checkbox" checked={form.noindex} onChange={(e) => setForm((f) => ({ ...f, noindex: e.target.checked }))} data-testid="post-input-noindex" />
                  <span className="text-[#4A4640]">noindex (ask Google not to list this post)</span>
                </label>
              </Field>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-[#D8CDBA]">
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="post-save">
                {saving ? "Saving…" : editing === "new" ? "Publish post" : "Save changes"}
              </button>
              <button onClick={cancel} className="btn-secondary" data-testid="post-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp =
  "w-full px-4 py-3 rounded-xl border border-[#D8CDBA] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] bg-[#FAF9F6]";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#736F66] mb-2 block">{label}</label>
      {children}
      {hint && <div className="text-xs text-[#736F66] mt-1.5">{hint}</div>}
    </div>
  );
}

function SectionHead({ children }) {
  return (
    <div className="pt-6 mt-2 border-t border-[#D8CDBA]">
      <div className="overline">{children}</div>
    </div>
  );
}

// Live counter: red when out of range, green when within, grey when empty
function Count({ n, min, max, unit, testid }) {
  const bad = n > 0 && ((max != null && n > max) || (min != null && n < min));
  const ok = n > 0 && !bad;
  return (
    <div className={`text-xs mt-1.5 ${bad ? "text-[#A83F32]" : ok ? "text-[#2C4C3B]" : "text-[#736F66]"}`} data-testid={testid}>
      {n}{max != null ? `/${max}` : ""} {unit}
      {min != null ? ` · recommended ${min}–${max}` : ""}
    </div>
  );
}
